import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { verifyByToken } from '../api/line';
import { TVerifyLineUserResponse } from '../types/line';
import useUserStore from '../store/useUserStore';
import { routes, storageKeys, TEXT_STRING } from '../constants';
import { registerTrustedRecallPublisher } from '../utils/chatRecallMessage';

// TODO: This is a test, not a real stream.

/**
 * Hook to authenticate user from URL token parameter
 * Used when app opens from talkmail redirect
 */
type UseTokenAuthOptions = {
  enabled?: boolean;
};

function useTokenAuth({ enabled = true }: UseTokenAuthOptions = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasNavigatedRef = useRef(false); // Track if we've already navigated (use ref to avoid re-renders)
  const hasAuthenticatedRef = useRef(false); // Track if we've already authenticated to prevent duplicate calls
  const isAuthenticatingRef = useRef(false); // Track if authentication is in progress to prevent duplicate calls
  const verifiedTokenRef = useRef<string | null>(null); // Track which token has been verified to prevent duplicate calls
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const setUser = useUserStore(state => state.setUser);
  const setMgmChannelId = useUserStore(state => state.setMgmChannelId);
  const setIsAuthenticated = useUserStore(state => state.setIsAuthenticated);
  const mgmChannelId = useUserStore(state => state.mgmChannelId);
  const isAuthenticated = useUserStore(state => state.isAuthenticated);

  // Check for error parameter from backend redirect
  useEffect(() => {
    if (!enabled) return;

    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError('Authentication failed. Please try again.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [enabled, searchParams, setSearchParams]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setError(null);
      return;
    }

    // Helper function to get token from URL
    const getTokenFromUrl = () => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      return {
        urlUcode: urlSearchParams.get('ucode'),
        errorParam: urlSearchParams.get('error') || searchParams.get('error'),
        errorMsg:
          urlSearchParams.get('error_msg') || searchParams.get('error_msg'),
        userLineId: parseInt(urlSearchParams.get('id') || '0'),
      };
    };

    // Get URL params once
    const { urlUcode } = getTokenFromUrl();
    const hasTokenInUrl = !!urlUcode;

    // Show loading immediately if we have token in URL and not already authenticated
    if (hasTokenInUrl && (!isAuthenticated || !mgmChannelId)) {
      setIsLoading(true);
    }

    // Skip if already authenticated in this session
    if (hasAuthenticatedRef.current && isAuthenticated && mgmChannelId) {
      setIsLoading(false);
      return;
    }

    // Skip if authentication is already in progress (prevent duplicate calls from StrictMode)
    if (isAuthenticatingRef.current) {
      return;
    }

    const authenticateFromToken = async () => {
      // Get URL params
      const { urlUcode, errorParam, errorMsg, userLineId } = getTokenFromUrl();
      const authToken = urlUcode;

      // Check if we need authentication
      const hasQueryParams = searchParams.get('id') || urlUcode;
      const needsAuth = !isAuthenticated || !mgmChannelId || hasQueryParams;

      // Check current route
      const isOnHomePage = location.pathname === '/' && !location.search;
      const isOnMemPath = location.pathname.startsWith('/mem');

      // If already authenticated with mgmChannelId, no query params, and already navigated, skip
      if (
        !needsAuth &&
        isAuthenticated &&
        mgmChannelId &&
        hasNavigatedRef.current
      ) {
        if (isOnHomePage || isOnMemPath) {
          return;
        }
      }

      // If already authenticated but haven't navigated yet, mark as navigated
      if (
        !needsAuth &&
        isAuthenticated &&
        mgmChannelId &&
        !hasNavigatedRef.current
      ) {
        if (isOnMemPath || isOnHomePage) {
          hasNavigatedRef.current = true;
          return;
        }
        hasNavigatedRef.current = true;
        navigate('/', { replace: true });
        return;
      }

      // Skip if this token has already been verified
      if (authToken && verifiedTokenRef.current === authToken) {
        return;
      }

      // Handle error from backend redirect
      if (errorParam) {
        console.error(
          '❌ [USE_TOKEN_AUTH] Error parameter detected:',
          errorParam,
          errorMsg,
        );
        setError(errorMsg || 'Authentication failed. Please try again.');
        searchParams.delete('error');
        searchParams.delete('error_msg');
        setSearchParams(searchParams, { replace: true });
        return;
      }

      // Only proceed if we have auth token (from URL)
      if (!authToken) {
        return;
      }

      // Set loading immediately when we start authentication
      setIsLoading(true);

      // Mark as authenticating to prevent duplicate calls
      if (isAuthenticatingRef.current) {
        setIsLoading(false);
        return;
      }
      isAuthenticatingRef.current = true;

      try {
        setError(null);

        // Mark token as being verified
        verifiedTokenRef.current = authToken;
        // Verify token with backend
        const result: TVerifyLineUserResponse = await verifyByToken(authToken, userLineId);

        // A survey owns its token flow. Ignore a chat-auth response that finishes
        // after the user has navigated to a survey.
        if (window.location.pathname.startsWith(routes.SURVEY_PATH_PREFIX)) {
          verifiedTokenRef.current = null;
          return;
        }

        if (result && result.user) {
          if (result.pubnubSystemBackendId) {
            registerTrustedRecallPublisher(result.pubnubSystemBackendId);
            localStorage.setItem(
              storageKeys.PUBNUB_SYSTEM_BACKEND_ID,
              result.pubnubSystemBackendId,
            );
          }

          // Set management channel ID if available
          if (result.channelId) {
            setMgmChannelId(result.channelId);
            localStorage.setItem('mgm_channel_id', result.channelId);
          }

          // Store access token for future API calls
          localStorage.setItem(storageKeys.ACCESS_TOKEN, authToken);
          localStorage.setItem(storageKeys.USER_ID, result.user.id.toString());

          // Store user info
          localStorage.setItem('line_user_id', result.user.lineUserId);
          localStorage.setItem(
            'line_user_name',
            result.user.name || result.user.lineDisplayName || '',
          );

          // Set user in store with uuid (for PubNub identification)
          if (result.user.uuid) {
            setUser({
              id: result.user.id,
              uuid: result.user.uuid,
              name: result.user.name || result.user.lineDisplayName || '',
              lineUserId: result.user.lineUserId,
            } as any);
          }

          // Mark as authenticated
          setIsAuthenticated(true);
          hasAuthenticatedRef.current = true;

          // Remove token, ucode and id from URL to clean up (if they exist)
          if (urlUcode || searchParams.get('id')) {
            if (!location.pathname.startsWith(routes.SURVEY_PATH_PREFIX)) {
              searchParams.delete('ucode');
              searchParams.delete('id');
              searchParams.delete('start');
              setSearchParams(searchParams, { replace: true });
            }
          }

          // Navigate to support tab (/mem/talk) after successful authentication,
          // except when current page needs to continue its own flow (e.g. richmenu redirect).
          hasNavigatedRef.current = true;
          if (
            location.pathname.startsWith(routes.RICHMENU_ADD_FRIEND) ||
            location.pathname.startsWith(routes.SURVEY_PATH_PREFIX)
          ) {
            return;
          }
          navigate(routes.TALK, { replace: true });
        } else {
          console.error('❌ [USE_TOKEN_AUTH] User not found in response');
          throw new Error(TEXT_STRING.ERROR.USER_NOT_FOUND);
        }
      } catch (err: any) {
        if (window.location.pathname.startsWith(routes.SURVEY_PATH_PREFIX)) {
          verifiedTokenRef.current = null;
          return;
        }

        console.error('❌ [USE_TOKEN_AUTH] Error authenticating from token:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
        });
        setError(err.message || 'Failed to authenticate from token');

        // Remove invalid token from URL (if they exist)
        if (urlUcode) {
          const currentPathname = window.location.pathname;
          const currentSearchParams = new URLSearchParams(
            window.location.search,
          );
          if (
            !currentPathname.startsWith(routes.SURVEY_PATH_PREFIX) &&
            currentSearchParams.get('ucode') === authToken
          ) {
            currentSearchParams.delete('ucode');
            currentSearchParams.delete('id');
            setSearchParams(currentSearchParams, { replace: true });
          }
        }
      } finally {
        setIsLoading(false);
        isAuthenticatingRef.current = false;
      }
    };

    // Execute immediately, no delay needed
    authenticateFromToken();
    // Note: setUser, setIsAuthenticated, setMgmChannelId are stable Zustand setters that don't change
    // location.search is derived from location.pathname which is already in deps
    // setSearchParams is stable from useSearchParams hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    location.pathname,
    searchParams,
    navigate,
    isAuthenticated,
    mgmChannelId,
  ]);

  return { isLoading, error };
}

export default useTokenAuth;
